import React, { useEffect, useState } from "react";
import { Check, Loader2, Plus, Search } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/CandidateCard";
import { Pill } from "@/components/common";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

export function PoolPickerDialog({ project, open, onOpenChange, onAdded }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [added, setAdded] = useState(new Set());
  const [busy, setBusy] = useState(null);
  const inProject = new Set(project.entries.map((e) => e.candidate.id));
  useEffect(() => { if (open) { setAdded(new Set()); setQ(""); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const h = setTimeout(() => api.get("/candidates", { params: { q: q.trim() || undefined, page_size: 15 } }).then((r) => setRes(r.data)).catch((e) => toast.error(errorDetail(e))), 300);
    return () => clearTimeout(h);
  }, [q, open]);
  const add = async (c) => {
    setBusy(c.id);
    try { await api.post(`/projects/${project.id}/entries`, { candidate_id: c.id }); setAdded(new Set([...added, c.id])); onAdded(); toast.success(t("Added to {{name}}", { name: project.title })); }
    catch (e) { toast.error(errorDetail(e)); } finally { setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[88vh] flex flex-col" data-testid="pool-picker-dialog">
        <DialogHeader><DialogTitle>{t("Add from the talent pool")}</DialogTitle><DialogDescription>{t("Search your private pool and the global catalog, then add profiles to {{name}}.", { name: project.title })}</DialogDescription></DialogHeader>
        <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Filter by name, title, company or city…")} className="h-9 pl-8" data-testid="pool-picker-search" /></div>
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1" data-testid="pool-picker-results">
          {!res ? <div className="h-20 rounded-lg shimmer bg-muted" /> : !res.items.length ? <p className="text-sm text-muted-foreground p-3">{t("No profiles")}</p>
            : res.items.map((c, i) => {
              const done = inProject.has(c.id) || added.has(c.id);
              return (
                <div key={c.id} className="flex items-center gap-3 rounded-lg border px-3 py-2" data-testid={`pool-picker-row-${i}`}>
                  <Avatar name={c.full_name} size={9} />
                  <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{c.full_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{[c.current_title, c.current_company, c.city].filter(Boolean).join(" · ")}</div></div>
                  <Pill tone={c.scope === "private" ? "indigo" : "default"}>{c.scope === "private" ? t("Private pool") : t("Global catalog")}</Pill>
                  <Button size="sm" variant={done ? "outline" : "default"} disabled={done || busy === c.id} onClick={() => add(c)} data-testid={`pool-picker-add-${i}`}>
                    {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}{done ? t("In project") : t("Add")}</Button>
                </div>
              );
            })}
          {res && res.total > res.items.length && <p className="text-[11px] text-muted-foreground text-center py-1">{t("{{count}} profiles in total — refine your search", { count: res.total })}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
